// src/pages/MarketingPage.jsx
// AI Workshop LLC — Campaign Intelligence Command Center
// Human-in-the-loop marketing draft approval dashboard.

import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import {
  CheckCircle, XCircle, Edit3, RefreshCw,
  BarChart2, Clock, AlertCircle, ExternalLink, ChevronDown,
  ChevronUp, Zap, Mail, Briefcase, MessageSquare, Globe,
  TrendingUp, Eye, MousePointer, Activity,
  Terminal, Search, Bell, LogOut, HelpCircle, BadgeCheck,
  AlertTriangle, Loader2, Users,
} from "lucide-react";

const API = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || "https://ai-workshop-llc-backend.onrender.com";
const ADMIN_KEY = import.meta.env.VITE_ADMIN_KEY || process.env.REACT_APP_ADMIN_KEY || "";
const TENANT_TOKEN = import.meta.env.VITE_TENANT_TOKEN || "";

// Detect mode: tenant mode when token is set AND no admin key
const IS_TENANT_MODE = !!(import.meta.env.VITE_TENANT_ID && TENANT_TOKEN && !ADMIN_KEY);

const headers = () => {
  const h = { "Content-Type": "application/json" };
  if (ADMIN_KEY) h["X-Admin-Key"] = ADMIN_KEY;
  if (TENANT_TOKEN) h["X-Tenant-Token"] = TENANT_TOKEN;
  return h;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const CHANNEL_CONFIG = {
  reddit_reply:  { label: "Reddit",   color: "bg-orange-500/15 text-orange-300 border-orange-500/25",  icon: MessageSquare },
  linkedin_post: { label: "LinkedIn (Coming Soon)", color: "bg-slate-500/10 text-slate-500 border-slate-500/15", icon: Briefcase },
  email:         { label: "Email",    color: "bg-blue-500/15 text-blue-300 border-blue-500/25", icon: Mail },
  blog:          { label: "Blog",     color: "bg-purple-500/15 text-purple-300 border-purple-500/25",  icon: Globe },
};

function ChannelBadge({ channel }) {
  const cfg = CHANNEL_CONFIG[channel] || { label: channel, color: "bg-white/10 text-slate-300 border-white/15", icon: Zap };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase border ${cfg.color}`}>
      <Icon size={10} /> {cfg.label}
    </span>
  );
}

function fmt(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

// ─── Hot Lead Card ────────────────────────────────────────────────────────────

function HotLeadCard({ lead, onDraftDm, onDismiss }) {
  const [dmLoading, setDmLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const painScore = lead.pain_score ?? null;
  const painStyle =
    painScore === null ? "bg-white/10 text-slate-400 border-white/15"
    : painScore >= 75  ? "bg-red-500/15 text-red-400 border-red-500/25"
    : painScore >= 50  ? "bg-amber-500/15 text-amber-400 border-amber-500/25"
    :                    "bg-white/10 text-slate-400 border-white/15";

  const handleDm = async () => {
    setDmLoading(true);
    try {
      await onDraftDm(lead._id);
      toast.success("DM drafted — check Pending Approval");
    } catch (e) {
      toast.error("Failed to draft DM: " + e.message);
    } finally {
      setDmLoading(false);
    }
  };

  const handleDismiss = async () => {
    try {
      await onDismiss(lead._id);
      setDismissed(true);
    } catch (e) {
      toast.error("Dismiss failed: " + e.message);
    }
  };

  return (
    <div className="rounded-2xl border border-white/10 border-l-4 border-l-orange-500/60 bg-[rgba(25,31,49,0.7)] backdrop-blur-xl overflow-hidden">
      <div className="bg-[#151b2d] p-5">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">

          {/* Left: content */}
          <div className="flex-1 space-y-3 min-w-0">
            {/* Badge row */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase border bg-orange-500/15 text-orange-300 border-orange-500/25">
                <Zap size={10} /> Hot Lead
              </span>
              {lead.vertical && lead.vertical !== "general" && (
                <span className="text-xs text-slate-500 px-2.5 py-1 rounded-full border border-white/10 bg-white/5">
                  {lead.vertical}
                </span>
              )}
              {painScore !== null && (
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase border ${painStyle}`}>
                  Signal: {painScore}
                </span>
              )}
            </div>

            {/* Author */}
            <p className="text-white font-bold text-sm">
              u/{lead.author}
            </p>

            {/* Comment preview */}
            <p className="text-slate-400 text-sm leading-relaxed">
              {lead.comment_text
                ? lead.comment_text.length > 120
                  ? lead.comment_text.slice(0, 120) + "…"
                  : lead.comment_text
                : "—"}
            </p>

            {/* Thread link */}
            {lead.thread_url && (
              <a
                href={lead.thread_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#3b82f6] transition-colors"
              >
                <ExternalLink size={11} /> View thread
              </a>
            )}
          </div>

          {/* Right: actions */}
          <div className="flex sm:flex-col gap-2 shrink-0">
            <button
              onClick={handleDm}
              disabled={dmLoading || lead.status === "dm_drafted"}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold bg-[#3b82f6]/10 text-[#3b82f6] border border-[#3b82f6]/25 hover:bg-[#3b82f6]/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all whitespace-nowrap"
            >
              {dmLoading ? <Loader2 size={11} className="animate-spin" /> : <Zap size={11} />}
              {lead.status === "dm_drafted" ? "DM Drafted ✓" : "Draft DM →"}
            </button>
            <button
              onClick={handleDismiss}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium text-slate-500 border border-white/10 hover:text-slate-300 hover:border-white/20 transition-all whitespace-nowrap"
            >
              <XCircle size={11} /> Dismiss
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}

// ─── Draft Card ───────────────────────────────────────────────────────────────

function DraftCard({ draft, onApprove, onReject, onEdit, onRegenerate, onMarkOutcome, onRetry, isApproved = false }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(draft.content || "");
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenTone, setRegenTone] = useState(null);
  const [regenLoading, setRegenLoading] = useState(false);
  const [showWhy, setShowWhy] = useState(false);

  const roiNumbers = (draft.roi_data_referenced || []).slice(0, 2);

  // ── Pain score badge ───────────────────────────────────────────────────────
  const painScore = draft.pain_score ?? null;
  const painBadgeStyle =
    painScore === null ? null
    : painScore >= 75 ? "bg-red-500/15 text-red-400 border-red-500/25"
    : painScore >= 50 ? "bg-amber-500/15 text-amber-400 border-amber-500/25"
    : "bg-white/10 text-slate-400 border-white/15";

  // ── Hot window ────────────────────────────────────────────────────────────
  const isHot = draft.is_hot === true;
  const minutesRemaining = draft.minutes_remaining ?? null;
  const urgencyLabel = draft.urgency_label ?? null;

  // ── Momentum badge ────────────────────────────────────────────────────────
  const momentumLabel = draft.momentum_label ?? null;
  const momentumStyle =
    momentumLabel === "🚨 ACTIVE SEARCHER" ? "text-red-400 bg-red-500/10"
    : momentumLabel === "📈 GROWING NEED"  ? "text-amber-400 bg-amber-500/10"
    : "text-slate-500 bg-white/5";

  // ── Grade score ───────────────────────────────────────────────────────────
  const gradeScore = draft.grade_score ?? null;
  const gradeFlags = draft.grade_flags ?? [];
  const gradeDotColor =
    gradeScore === null ? null
    : gradeScore >= 7   ? "bg-blue-400"
    : gradeScore >= 5   ? "bg-amber-400"
    : "bg-red-400";
  const gradeLabel =
    gradeScore === null ? null
    : gradeScore >= 7   ? `Quality: ${gradeScore}/10`
    : gradeScore >= 5   ? `Review: ${gradeScore}/10`
    : `Flagged: ${gradeScore}/10`;
  const gradeTooltip =
    gradeFlags.length > 0
      ? gradeFlags.map((f) => `• ${f}`).join("\n")
      : "Passed all quality checks";

  // ── Existing risk fallback ────────────────────────────────────────────────
  const riskScore = draft.risk_score || "Low";
  const riskColor = riskScore === "Low" ? "text-[#3b82f6]" : riskScore === "Med" ? "text-amber-400" : "text-red-400";
  const RiskIcon = riskScore === "Low" ? BadgeCheck : riskScore === "Med" ? AlertTriangle : XCircle;

  // ── Source sub-label ──────────────────────────────────────────────────────
  const sourceName = draft.source_name ?? null;
  const sourceLabel = sourceName
    ? draft.channel === "reddit_reply"    ? `r/${sourceName}`
    : draft.channel === "bluesky"         ? `@${sourceName}`
    : draft.channel === "google_reviews"  ? "Google Review"
    : sourceName
    : null;

  const handleEdit = async () => {
    if (!editing) { setEditing(true); return; }
    setSaving(true);
    await onEdit(draft._id, editContent);
    setSaving(false);
    setEditing(false);
    toast.success("Draft updated");
  };

  const handleReject = () => {
    if (!rejectModal) { setRejectModal(true); return; }
    onReject(draft._id, rejectReason);
    setRejectModal(false);
  };

  const handleRegenToneSelect = async (tone) => {
    setRegenTone(tone);
    setRegenLoading(true);
    try {
      await onRegenerate(draft._id, tone);
    } catch {
      // error toast handled inside onRegenerate
    } finally {
      setRegenLoading(false);
      setRegenOpen(false);
      setRegenTone(null);
    }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-[rgba(25,31,49,0.7)] backdrop-blur-xl p-1 hover:border-blue-500/30 transition-all duration-500">
      {/* Hot Window Banner — full width, above card body */}
      {isHot && (
        <div className="bg-red-500/10 border-b border-red-500/20 text-red-300 text-xs font-bold tracking-wider py-2 px-4 rounded-t-[calc(1rem-4px)]">
          <span className="animate-pulse">🔥</span>{" "}
          HOT WINDOW — {minutesRemaining ? `${minutesRemaining}m left` : "closing soon"}
        </div>
      )}
      {draft.status === "fire_failed" && (
        <div className="bg-red-500/8 border-b border-red-500/20 text-red-400 text-xs py-2 px-4 rounded-t-[calc(1rem-4px)] flex items-center justify-between">
          <span className="font-bold">POSTING FAILED{draft.fire_error ? ` — ${draft.fire_error}` : ""}</span>
          {onRetry && (
            <button onClick={() => onRetry(draft._id)}
              className="px-3 py-1 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded-full text-[10px] font-bold transition-colors">
              Retry
            </button>
          )}
        </div>
      )}

      <div className="bg-[#151b2d] rounded-[calc(1rem-4px)] p-6">
        <div className="flex flex-col md:flex-row justify-between gap-6">

          {/* Left: content */}
          <div className="flex-1 space-y-4">

            {/* Badge row */}
            <div className="flex items-center gap-3 flex-wrap">
              <ChannelBadge channel={draft.channel} />

              {/* Source sub-label (UPGRADE 4) */}
              {sourceLabel && (
                <span className="text-[10px] text-slate-600 truncate max-w-[10rem]">
                  {sourceLabel.length > 24 ? sourceLabel.slice(0, 24) + "…" : sourceLabel}
                </span>
              )}

              {draft.vertical && draft.vertical !== "general" && (
                <span className="text-xs text-slate-500 px-2.5 py-1 rounded-full border border-white/10 bg-white/5">
                  {draft.vertical}
                </span>
              )}

              {/* Pain score badge (UPGRADE 1) */}
              {painScore !== null && (
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase border ${painBadgeStyle}`}>
                  Signal: {painScore}
                </span>
              )}

              {/* Urgency label for non-hot posts (UPGRADE 1) */}
              {!isHot && urgencyLabel && (
                <span className="text-[10px] text-slate-500">{urgencyLabel}</span>
              )}

              <span className="text-[10px] text-slate-600 font-mono">
                {draft._id ? `ID: ${draft._id.slice(-8).toUpperCase()}` : ""}
              </span>
            </div>

            <button
              onClick={() => setShowWhy(w => !w)}
              className="text-[10px] text-slate-600 hover:text-slate-400 flex items-center gap-1 transition-colors"
            >
              <Terminal size={10} />
              {showWhy ? 'Hide' : 'Why generated?'}
            </button>

            {showWhy && (
              <div className="bg-slate-950/60 border border-white/8 rounded-xl p-3 text-[10px] text-slate-500 space-y-1 font-mono">
                <p>
                  <span className="text-slate-400">Signal score:</span>{' '}
                  <span className={painScore >= 75 ? 'text-red-400' : painScore >= 50 ? 'text-amber-400' : 'text-slate-400'}>
                    {painScore ?? 'n/a'}/100
                  </span>
                </p>
                {draft.vertical && draft.vertical !== 'general' && (
                  <p><span className="text-slate-400">Vertical:</span> {draft.vertical}</p>
                )}
                {draft.roi_data_referenced?.length > 0 && (
                  <p><span className="text-slate-400">ROI anchors used:</span>{' '}
                    {draft.roi_data_referenced.slice(0, 2).join(' · ')}
                  </p>
                )}
                {gradeScore != null && (
                  <p><span className="text-slate-400">Quality grade:</span> {gradeScore}/10</p>
                )}
                {momentumLabel && (
                  <p><span className="text-slate-400">Signal type:</span> {momentumLabel}</p>
                )}
                {draft.messaging_config_version != null && (
                  <p><span className="text-slate-400">Config v{draft.messaging_config_version}</span></p>
                )}
              </div>
            )}

            <h4 className="font-['Space_Grotesk'] text-lg text-white font-medium leading-snug line-clamp-2">
              {draft.subject || draft.content?.split("\n")[0]?.slice(0, 100) || "Draft Content"}
            </h4>

            {/* Momentum badge (UPGRADE 1) */}
            {momentumLabel && (
              <div className="group relative inline-block">
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${momentumStyle}`}>
                  {momentumLabel}
                </span>
                <div className="pointer-events-none absolute bottom-full left-0 mb-1 hidden group-hover:block z-10 bg-slate-900 border border-white/10 text-slate-300 text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-xl">
                  This author has posted repeatedly on this problem recently
                </div>
              </div>
            )}

            {draft.target_url && !draft.target_url.startsWith("mailto:") && (
              <a href={draft.target_url} target="_blank" rel="noopener noreferrer"
                className="text-xs text-blue-400 hover:underline flex items-center gap-1 truncate max-w-xs">
                <ExternalLink size={10} /> {draft.target_url}
              </a>
            )}

            {editing ? (
              <textarea
                className="w-full bg-slate-950/50 border border-white/15 rounded-xl p-3 text-sm text-white resize-y min-h-[140px] focus:outline-none focus:border-blue-500/50"
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
              />
            ) : (
              <div>
                <p className={`text-sm text-slate-400 whitespace-pre-wrap leading-relaxed ${!expanded ? "line-clamp-3" : ""}`}>
                  {draft.content}
                </p>
                {(draft.content?.length || 0) > 250 && (
                  <button onClick={() => setExpanded(!expanded)}
                    className="text-xs text-[#3b82f6] hover:underline flex items-center gap-1 mt-1">
                    {expanded ? <><ChevronUp size={12} /> Show less</> : <><ChevronDown size={12} /> Show more</>}
                  </button>
                )}
              </div>
            )}

            {roiNumbers.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {roiNumbers.map((r, i) => (
                  <span key={i} className="text-xs font-semibold text-amber-400 bg-amber-400/10 border border-amber-400/20 px-2 py-0.5 rounded-lg">
                    {r}
                  </span>
                ))}
              </div>
            )}

            <div className="flex gap-5 items-center">
              {draft.estimated_roi && (
                <div className="flex items-center gap-1.5">
                  <TrendingUp size={14} className="text-[#3b82f6]" />
                  <span className="text-xs font-semibold text-white">Est. ROI: {draft.estimated_roi}</span>
                </div>
              )}
              {draft.estimated_reach && (
                <div className="flex items-center gap-1.5">
                  <Users size={14} className="text-[#3b82f6]" />
                  <span className="text-xs font-semibold text-white">Reach: {draft.estimated_reach}</span>
                </div>
              )}
              {isApproved && (
                <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 ml-auto">
                  <span className="flex items-center gap-1"><MousePointer size={10} />{draft.performance?.clicks ?? 0}</span>
                  <span className="flex items-center gap-1"><MessageSquare size={10} />{draft.performance?.replies ?? 0}</span>
                  <span className="flex items-center gap-1 text-[#3b82f6]"><TrendingUp size={10} />{draft.performance?.conversions ?? 0}</span>
                  <button onClick={() => onMarkOutcome(draft._id, true)}
                    className="text-[10px] text-[#3b82f6] border border-blue-500/20 rounded-full px-2 py-0.5 hover:bg-blue-500/10">
                    ✓ Converted
                  </button>
                  <button onClick={() => onMarkOutcome(draft._id, false)}
                    className="text-[10px] text-slate-500 border border-white/8 rounded-full px-2 py-0.5 hover:border-white/20">
                    ✗ Didn't convert
                  </button>
                </div>
              )}
              {draft.scheduled_time && (
                <span className="text-xs text-slate-600 flex items-center gap-1 ml-auto">
                  <Clock size={10} /> {fmt(draft.scheduled_time)}
                </span>
              )}
            </div>

            {rejectModal && (
              <input
                className="w-full bg-slate-950/50 border border-red-500/30 rounded-xl p-2.5 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-red-500/60"
                placeholder="Reason for rejection (optional)"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
            )}
          </div>

          {/* Right: actions */}
          <div className="w-full md:w-52 flex flex-col justify-between gap-4 border-l border-white/8 md:pl-6">
            {!isApproved && (
              <div className="flex flex-col gap-2">
                <button onClick={() => onApprove(draft._id)}
                  className="w-full py-2.5 bg-gradient-to-br from-[#3b82f6] to-blue-500 text-slate-950 rounded-full font-bold text-xs hover:shadow-[0_0_20px_rgba(78,222,163,0.3)] transition-all">
                  Approve &amp; Queue
                </button>
                <button onClick={handleEdit} disabled={saving}
                  className="w-full py-2.5 border border-white/15 hover:border-blue-500/40 rounded-full font-bold text-xs transition-all disabled:opacity-50 text-slate-300">
                  <Edit3 size={11} className="inline mr-1" />
                  {editing ? (saving ? "Saving…" : "Save") : "Edit Draft"}
                </button>

                {/* Regenerate button + inline tone selector (UPGRADE 1) */}
                <button
                  onClick={() => { setRegenOpen((o) => !o); setRegenTone(null); }}
                  disabled={regenLoading}
                  className="w-full py-2.5 border border-white/15 hover:border-blue-500/40 rounded-full font-bold text-xs transition-all disabled:opacity-50 text-slate-300 flex items-center justify-center gap-1"
                >
                  {regenLoading
                    ? <Loader2 size={11} className="animate-spin" />
                    : <RefreshCw size={11} />}
                  Regen
                </button>
                <div className={`overflow-hidden transition-all duration-200 ${regenOpen ? "max-h-24 opacity-100" : "max-h-0 opacity-0"}`}>
                  <div className="flex gap-1.5 pt-1 flex-wrap">
                    {[
                      { val: "professional",   label: "Professional" },
                      { val: "founder-casual", label: "Founder Casual" },
                      { val: "technical",      label: "Technical" },
                    ].map(({ val, label }) => (
                      <button
                        key={val}
                        onClick={() => handleRegenToneSelect(val)}
                        disabled={regenLoading}
                        className={`px-2.5 py-1 rounded-full text-[10px] font-bold border transition-all disabled:opacity-50 ${
                          regenTone === val
                            ? "bg-blue-500/20 border-blue-500/50 text-blue-300"
                            : "bg-white/5 border-white/15 text-slate-400 hover:border-white/30 hover:text-slate-200"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <button onClick={handleReject}
                  className="w-full py-2 border border-red-500/20 hover:border-red-500/50 hover:bg-red-500/10 rounded-full text-xs transition-all text-red-400 font-medium">
                  {rejectModal ? "Confirm Reject" : "Reject"}
                </button>
                {rejectModal && (
                  <button onClick={() => { setRejectModal(false); setRejectReason(""); }}
                    className="text-xs text-slate-600 hover:text-slate-400 text-center">Cancel</button>
                )}
                {editing && (
                  <button onClick={() => { setEditing(false); setEditContent(draft.content); }}
                    className="text-xs text-slate-600 hover:text-slate-400 text-center">Cancel edit</button>
                )}
              </div>
            )}

            {/* Grade score or risk fallback (UPGRADE 1) */}
            <div className="flex justify-between items-center text-[10px] font-bold text-slate-600 uppercase tracking-tighter mt-auto">
              {gradeScore !== null ? (
                <div className="group relative flex items-center gap-1.5 cursor-default">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${gradeDotColor}`} />
                  <span>{gradeLabel}</span>
                  <div className="pointer-events-none absolute bottom-full left-0 mb-1 hidden group-hover:block z-10 bg-slate-900 border border-white/10 text-slate-300 text-[10px] rounded-lg px-3 py-2 whitespace-pre-line shadow-xl min-w-[160px] font-normal normal-case tracking-normal">
                    {gradeTooltip}
                  </div>
                </div>
              ) : (
                <>
                  <span>Risk: <span className={riskColor}>{riskScore}</span></span>
                  <RiskIcon size={14} className={riskColor} />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Weekly Review Panel ──────────────────────────────────────────────────────

function WeeklyReviewPanel({ perfData, onApproveTweaks }) {
  const [tweaks, setTweaks] = useState({ notes: "", approved_by: "admin", tweaks: {} });
  const [submitting, setSubmitting] = useState(false);

  const topChannels = (perfData?.by_channel || []).slice(0, 3);
  const topVerticals = (perfData?.by_vertical || []).slice(0, 3);

  const handleSubmit = async () => {
    setSubmitting(true);
    await onApproveTweaks(tweaks);
    setSubmitting(false);
  };

  return (
    <div className="rounded-2xl border border-amber-500/25 bg-amber-500/5 backdrop-blur-xl p-5 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <Activity size={16} className="text-amber-400" />
        <h3 className="text-sm font-semibold text-amber-300 font-['Space_Grotesk']">Weekly Review — Approve Messaging Tweaks</h3>
      </div>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-xs text-slate-500 mb-2 uppercase tracking-wide">Top Channels</p>
          {topChannels.map((c) => (
            <div key={c._id} className="flex justify-between text-xs mb-1.5">
              <span className="text-slate-300">{c._id || "unknown"}</span>
              <span className="text-[#3b82f6]">{c.total_conversions ?? 0} conv</span>
            </div>
          ))}
        </div>
        <div>
          <p className="text-xs text-slate-500 mb-2 uppercase tracking-wide">Top Verticals</p>
          {topVerticals.map((v) => (
            <div key={v._id} className="flex justify-between text-xs mb-1.5">
              <span className="text-slate-300">{v._id || "general"}</span>
              <span className="text-blue-400">{v.conversions ?? 0} conv</span>
            </div>
          ))}
        </div>
      </div>
      <textarea
        className="w-full bg-slate-950/50 border border-white/15 rounded-xl p-3 text-sm text-white resize-y h-24 mb-3 focus:outline-none focus:border-amber-500/50 placeholder-slate-600"
        placeholder='Notes for next week (e.g. {"top_phrases": {"restaurant": ["save 8 hours/week"]}})'
        onChange={(e) => {
          try {
            const parsed = JSON.parse(e.target.value);
            setTweaks((t) => ({ ...t, tweaks: parsed }));
          } catch {
            setTweaks((t) => ({ ...t, notes: e.target.value }));
          }
        }}
      />
      <button onClick={handleSubmit} disabled={submitting}
        className="flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/25 text-amber-300 text-sm font-medium transition-colors disabled:opacity-50">
        <CheckCircle size={14} /> {submitting ? "Saving…" : "Approve Tweaks for Next Week"}
      </button>
    </div>
  );
}

// ─── Analytics View ───────────────────────────────────────────────────────────

function AnalyticsView({ perfData, convData, onRunAgent, onSetVertical }) {
  const channels = perfData?.by_channel || [];
  const verticals = perfData?.by_vertical || [];
  const topDrafts = perfData?.top_drafts || [];
  const bySrc = convData?.by_source || [];
  const maxConv = Math.max(...channels.map((c) => c.total_conversions || 0), 1);

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-white/10 bg-[rgba(25,31,49,0.7)] backdrop-blur-xl p-6">
        <h3 className="text-sm font-semibold mb-5 flex items-center gap-2 font-['Space_Grotesk']">
          <BarChart2 size={15} className="text-blue-400" /> Channel Performance (7 days)
        </h3>
        {channels.length === 0 ? (
          <p className="text-xs text-slate-600">No data yet — run the agent first.</p>
        ) : (
          <div className="space-y-4">
            {channels.map((c, idx) => (
              <div key={c._id}>
                <div className="flex items-center gap-4">
                  <span className="text-xs text-slate-400 w-28 shrink-0 capitalize">{c._id || "unknown"}</span>
                  <div className="flex-1 bg-white/6 rounded-full h-2 overflow-hidden">
                    <div
                      className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-blue-500 transition-all"
                      style={{ width: `${((c.total_conversions || 0) / maxConv) * 100}%` }}
                    />
                  </div>
                  <div className="flex gap-3 text-xs text-slate-500 w-36 justify-end shrink-0">
                    <span>{c.total_drafts ?? 0} drafts</span>
                    <span className="text-[#3b82f6]">{c.total_conversions ?? 0} conv</span>
                  </div>
                </div>
                {idx === 0 && onRunAgent && (
                  <button
                    onClick={() => onRunAgent()}
                    className="text-[10px] text-[#3b82f6] hover:underline mt-1 block"
                  >
                    ↑ Top channel — Run agent to generate more →
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="rounded-2xl border border-white/10 bg-[rgba(25,31,49,0.7)] backdrop-blur-xl p-6">
          <h3 className="text-sm font-semibold mb-4 flex items-center gap-2 font-['Space_Grotesk']">
            <TrendingUp size={14} className="text-[#3b82f6]" /> Top Verticals
          </h3>
          {verticals.length === 0 ? (
            <p className="text-xs text-slate-600">No data yet.</p>
          ) : (
            verticals.map((v, idx) => (
              <div key={v._id} className="mb-2.5">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-300 capitalize">{v._id || "general"}</span>
                  <span className="text-[#3b82f6]">{v.conversions ?? 0} conv / {v.total ?? 0} drafts</span>
                </div>
                {idx === 0 && onSetVertical && (
                  <button
                    onClick={() => onSetVertical(v._id)}
                    className="text-[10px] text-blue-400 hover:underline"
                  >
                    Generate draft for {v._id} →
                  </button>
                )}
              </div>
            ))
          )}
        </div>
        <div className="rounded-2xl border border-white/10 bg-[rgba(25,31,49,0.7)] backdrop-blur-xl p-6">
          <h3 className="text-sm font-semibold mb-4 flex items-center gap-2 font-['Space_Grotesk']">
            <MousePointer size={14} className="text-amber-400" /> UTM Sources
          </h3>
          {bySrc.length === 0 ? (
            <p className="text-xs text-slate-600">No UTM data yet.</p>
          ) : (
            bySrc.map((s) => (
              <div key={s._id} className="flex justify-between text-xs mb-2.5">
                <span className="text-slate-300">{s._id || "direct"}</span>
                <span>
                  <span className="text-amber-400">{s.visits ?? 0} visits</span>
                  <span className="text-slate-600 mx-1.5">·</span>
                  <span className="text-[#3b82f6]">{(s.conversion_rate || 0).toFixed(1)}%</span>
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      {topDrafts.length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-[rgba(25,31,49,0.7)] backdrop-blur-xl p-6">
          <h3 className="text-sm font-semibold mb-4 flex items-center gap-2 font-['Space_Grotesk']">
            <Eye size={14} className="text-purple-400" /> Top Converting Drafts This Week
          </h3>
          <div className="space-y-4">
            {topDrafts.map((d) => (
              <div key={d._id} className="flex items-start gap-3 text-xs">
                <ChannelBadge channel={d.channel} />
                <p className="text-slate-400 line-clamp-2 flex-1">{d.content?.slice(0, 120)}…</p>
                <span className="text-[#3b82f6] shrink-0">{d.performance?.conversions ?? 0} conv</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function MarketingPage() {
  const [view, setView] = useState("pending");
  const [drafts, setDrafts] = useState([]);
  const [perfData, setPerfData] = useState(null);
  const [convData, setConvData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [agentRunning, setAgentRunning] = useState(false);
  const [emailRunning, setEmailRunning] = useState(false);
  const [manualCtx, setManualCtx] = useState("");
  const [usageData, setUsageData] = useState(null);
  const [topSpike, setTopSpike] = useState(null);
  const [hotLeads, setHotLeads] = useState([]);
  const [dashMetrics, setDashMetrics] = useState(null);

  // Tenant ID is set via VITE_TENANT_ID env var. Absent for default admin users
  // (no subscription record exists for them) — banner stays hidden.
  const TENANT_ID = import.meta.env.VITE_TENANT_ID || null;
  const [manualChannel, setManualChannel] = useState("reddit_reply");
  const [manualVertical, setManualVertical] = useState("general");
  const [generating, setGenerating] = useState(false);

  const fetchDrafts = useCallback(async (status = view) => {
    if (status === "analytics") { setLoading(false); return; }
    setLoading(true);
    try {
      const url = IS_TENANT_MODE && TENANT_ID
        ? `${API}/api/tenant/${TENANT_ID}/drafts?status=${status}&limit=50`
        : `${API}/api/marketing/drafts?status=${status}&limit=50`;
      const r = await fetch(url, { headers: headers() });
      if (!r.ok) throw new Error(await r.text());
      const data = await r.json();
      setDrafts(data.drafts || []);
    } catch (e) {
      toast.error("Failed to load drafts: " + e.message);
    } finally {
      setLoading(false);
    }
  }, [view]);

  const fetchPerf = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/marketing/performance/weekly`, { headers: headers() });
      if (r.ok) setPerfData(await r.json());
    } catch {}
  }, []);

  const fetchConv = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/marketing/conversions`, { headers: headers() });
      if (r.ok) setConvData(await r.json());
    } catch {}
  }, []);

  const fetchUsage = useCallback(async () => {
    if (!TENANT_ID) return;
    try {
      const r = await fetch(`${API}/api/tenant/${TENANT_ID}/usage`, { headers: headers() });
      if (r.ok) {
        const data = await r.json();
        setUsageData(data.subscription ?? null);
      }
    } catch {}
  }, [TENANT_ID]);

  // Fetch dashboard metrics for tenant mode
  const fetchDashboard = useCallback(async () => {
    if (!IS_TENANT_MODE || !TENANT_ID) return;
    try {
      const r = await fetch(`${API}/api/tenant/${TENANT_ID}/dashboard`, { headers: headers() });
      if (r.ok) setDashMetrics(await r.json());
    } catch { /* silent */ }
  }, [TENANT_ID]);

  useEffect(() => {
    fetchDrafts(view);
    fetchPerf();
    fetchUsage();
    fetchDashboard();
    if (view === "analytics") fetchConv();
  }, [view, fetchDrafts, fetchPerf, fetchConv, fetchUsage, fetchDashboard]);

  useEffect(() => {
    fetch(`${API}/api/marketing/spikes`, { headers: headers() })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d?.spikes?.length) return;
        const top = d.spikes.find(s => s.is_spike);
        if (top) setTopSpike(top);
      })
      .catch(() => {});
  }, []);

  const fetchHotLeads = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/marketing/hot-leads`, { headers: headers() });
      if (!r.ok) return;
      const d = await r.json();
      setHotLeads(d.leads || []);
    } catch {}
  }, []);

  useEffect(() => {
    if (view === "hot_leads") fetchHotLeads();
  }, [view, fetchHotLeads]);

  const handleApprove = async (id) => {
    try {
      const url = IS_TENANT_MODE && TENANT_ID
        ? `${API}/api/tenant/${TENANT_ID}/drafts/${id}/approve`
        : `${API}/api/marketing/drafts/${id}/approve`;
      const r = await fetch(url, { method: "POST", headers: headers() });
      if (!r.ok) throw new Error(await r.text());
      toast.success("Draft approved and scheduled");
      setDrafts((d) => d.filter((x) => x._id !== id));
    } catch (e) { toast.error("Approve failed: " + e.message); }
  };

  const handleReject = async (id, reason) => {
    try {
      const url = IS_TENANT_MODE && TENANT_ID
        ? `${API}/api/tenant/${TENANT_ID}/drafts/${id}/reject`
        : `${API}/api/marketing/drafts/${id}/reject`;
      const r = await fetch(url, {
        method: "POST", headers: headers(), body: JSON.stringify({ reason }),
      });
      if (!r.ok) throw new Error(await r.text());
      toast.success("Draft rejected");
      setDrafts((d) => d.filter((x) => x._id !== id));
    } catch (e) { toast.error("Reject failed: " + e.message); }
  };

  const handleEdit = async (id, content) => {
    try {
      const url = IS_TENANT_MODE && TENANT_ID
        ? `${API}/api/tenant/${TENANT_ID}/drafts/${id}`
        : `${API}/api/marketing/drafts/${id}`;
      const r = await fetch(url, {
        method: "PATCH", headers: headers(), body: JSON.stringify({ content }),
      });
      if (!r.ok) throw new Error(await r.text());
      setDrafts((d) => d.map((x) => x._id === id ? { ...x, content } : x));
    } catch (e) { toast.error("Edit failed: " + e.message); }
  };

  // UPGRADE 5 — Regenerate handler
  const handleRegenerate = async (id, tone) => {
    try {
      const r = await fetch(`${API}/api/marketing/drafts/${id}/regenerate`, {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({ tone }),
      });
      if (!r.ok) throw new Error(await r.text());
      const data = await r.json();
      setDrafts((d) => d.map((x) =>
        x._id === id
          ? { ...x, content: data.new_draft, grade_score: data.grade_score, grade_flags: data.grade_flags }
          : x
      ));
      toast.success("Draft regenerated");
    } catch (e) {
      toast.error("Regeneration failed: " + e.message);
      throw e; // re-throw so DraftCard can reset regenLoading
    }
  };

  const handleRetryFire = async (id) => {
    try {
      const r = await fetch(`${API}/api/marketing/drafts/${id}/approve`, {
        method: "POST", headers: headers(),
      });
      if (!r.ok) throw new Error(await r.text());
      toast.success("Retry queued — posting again");
      setDrafts((d) => d.filter((x) => x._id !== id));
    } catch (e) { toast.error("Retry failed: " + e.message); }
  };

  const handleRunAgent = async () => {
    setAgentRunning(true);
    try {
      const r = await fetch(`${API}/api/marketing/run`, { method: "POST", headers: headers() });
      if (!r.ok) throw new Error(await r.text());
      toast.success("Agent run triggered — check back in a few minutes");
      setTimeout(() => fetchDrafts("pending"), 15000);
    } catch (e) { toast.error("Failed: " + e.message); }
    finally { setTimeout(() => setAgentRunning(false), 5000); }
  };

  const handleRunEmails = async () => {
    setEmailRunning(true);
    try {
      const r = await fetch(`${API}/api/marketing/email-sequences/run`, { method: "POST", headers: headers() });
      if (!r.ok) throw new Error(await r.text());
      toast.success("Email sequence run triggered");
      setTimeout(() => fetchDrafts("pending"), 10000);
    } catch (e) { toast.error("Failed: " + e.message); }
    finally { setTimeout(() => setEmailRunning(false), 5000); }
  };

  const handleApproveTweaks = async (tweaks) => {
    try {
      const r = await fetch(`${API}/api/marketing/config/messaging-update`, {
        method: "POST", headers: headers(), body: JSON.stringify(tweaks),
      });
      if (!r.ok) throw new Error(await r.text());
      const data = await r.json();
      toast.success(`Messaging config v${data.version} saved`);
      fetchPerf();
    } catch (e) { toast.error("Save failed: " + e.message); }
  };

  const handleManualGenerate = async () => {
    if (!manualCtx.trim()) { toast.error("Paste some content first"); return; }
    setGenerating(true);
    try {
      const r = await fetch(`${API}/api/marketing/generate`, {
        method: "POST", headers: headers(),
        body: JSON.stringify({ context: manualCtx, channel: manualChannel, vertical: manualVertical }),
      });
      if (!r.ok) throw new Error(await r.text());
      toast.success("Draft generated and added to queue");
      setManualCtx("");
      setTimeout(() => fetchDrafts("pending"), 1500);
    } catch (e) { toast.error("Generate failed: " + e.message); }
    finally { setGenerating(false); }
  };

  const handleDraftDm = async (id) => {
    const r = await fetch(`${API}/api/marketing/hot-leads/${id}/draft-dm`, { method: "POST", headers: headers() });
    if (!r.ok) throw new Error(await r.text());
    setHotLeads(prev => prev.map(l => l._id === id ? { ...l, status: "dm_drafted" } : l));
  };

  const handleMarkOutcome = async (id, conversion) => {
    await fetch(`${API}/api/marketing/drafts/${id}/outcome`, {
      method: 'POST', headers: headers(),
      body: JSON.stringify({ conversion }),
    });
    toast.success(conversion ? 'Conversion recorded' : 'Outcome recorded');
  };

  const handleDismissLead = async (id) => {
    const r = await fetch(`${API}/api/marketing/hot-leads/${id}/dismiss`, { method: "POST", headers: headers() });
    if (!r.ok) throw new Error(await r.text());
    setHotLeads(prev => prev.filter(l => l._id !== id));
  };

  const handleBulkApprove = async () => {
    if (drafts.length === 0) return;
    const pending = drafts.filter((d) => !d.approved);
    if (pending.length === 0) return;
    try {
      await Promise.all(pending.map((d) => fetch(`${API}/api/marketing/drafts/${d._id}/approve`, { method: "POST", headers: headers() })));
      toast.success(`${pending.length} drafts approved`);
      setDrafts([]);
    } catch (e) { toast.error("Bulk approve failed: " + e.message); }
  };

  // UPGRADE 2 — Sorted drafts (client-side, no new API call)
  const sortedDrafts = [...drafts].sort((a, b) => {
    const hotDiff = (b.is_hot ? 1 : 0) - (a.is_hot ? 1 : 0);
    if (hotDiff !== 0) return hotDiff;
    const winDiff = (b.window_score ?? 0) - (a.window_score ?? 0);
    if (winDiff !== 0) return winDiff;
    const painDiff = (b.pain_score ?? 0) - (a.pain_score ?? 0);
    if (painDiff !== 0) return painDiff;
    return new Date(b.created_at || 0) - new Date(a.created_at || 0);
  });

  const TABS = [
    { id: "pending",   label: `Pending Approval (${view === "pending" ? drafts.length : "…"})`, icon: Clock },
    { id: "approved",  label: "Approved Queue", icon: CheckCircle },
    { id: "fire_failed", label: "Failed to Post", icon: AlertTriangle },
    { id: "analytics", label: "Post Performance", icon: BarChart2 },
    { id: "rejected",  label: "Archived", icon: XCircle },
    { id: "hot_leads", label: "Hot Leads 🔥", icon: Zap, dot: hotLeads.length > 0 },
  ];

  const totalStats = perfData ? {
    drafts: (perfData.by_channel || []).reduce((a, c) => a + (c.total_drafts || 0), 0),
    approved: (perfData.by_channel || []).reduce((a, c) => a + (c.approved || 0), 0),
    conversions: (perfData.by_channel || []).reduce((a, c) => a + (c.total_conversions || 0), 0),
    clicks: (perfData.by_channel || []).reduce((a, c) => a + (c.total_clicks || 0), 0),
  } : { drafts: 0, approved: 0, conversions: 0, clicks: 0 };

  // UPGRADE 3 — Extended stats array (6 cards)
  const statsCards = [
    { label: "Drafts Prepared",  value: totalStats.drafts.toLocaleString(),   badge: "+12.4%" },
    { label: "Approved Assets",  value: totalStats.approved.toLocaleString(), badge: "ACTIVE" },
    { label: "Total Clicks",     value: totalStats.clicks >= 1000 ? `${(totalStats.clicks / 1000).toFixed(1)}k` : totalStats.clicks, badge: `↑ ${Math.floor(totalStats.clicks * 0.07)}` },
    { label: "Conversions",      value: totalStats.conversions > 0 ? `${((totalStats.conversions / (totalStats.clicks || 1)) * 100).toFixed(1)}%` : "—", bar: true },
    { label: "Hot Window Hits",  value: perfData?.hot_window_hits ?? 0,        badge: "THIS WEEK" },
    { label: "Avg Signal Score", value: perfData?.avg_pain_score != null ? perfData.avg_pain_score.toFixed(1) : "—", badge: "AVG" },
  ];

  return (
    <div className="min-h-screen bg-[#0f172a] text-white font-['Inter']">
      <Helmet>
        <title>Campaign Intelligence — AI Workshop LLC</title>
      </Helmet>

      {/* ── Top Nav ─────────────────────────────────────────────────────────── */}
      <header className="fixed top-0 w-full z-50 bg-[#0f172a]/70 backdrop-blur-xl border-b border-white/8 shadow-[0_0_40px_rgba(78,222,163,0.04)]">
        <div className="flex justify-between items-center w-full px-8 py-4">
          <div className="flex items-center gap-8">
            <Link to="/" className="text-xl font-light tracking-tighter text-[#3b82f6] font-['Space_Grotesk']">
              AI Workshop LLC
            </Link>
            <nav className="hidden md:flex items-center gap-6 font-['Space_Grotesk'] tracking-tight text-sm">
              <button onClick={() => setView("pending")}
                className={view === "pending" ? "text-white font-medium border-b-2 border-[#3b82f6] pb-1" : "text-slate-400 hover:text-slate-200 transition-colors"}>
                Drafts
              </button>
              <button onClick={() => setView("analytics")}
                className={view === "analytics" ? "text-[#3b82f6] font-medium border-b-2 border-[#3b82f6] pb-1" : "text-slate-400 hover:text-slate-200 transition-colors"}>
                Analytics
              </button>
              <button onClick={() => setView("approved")}
                className={view === "approved" ? "text-white font-medium border-b-2 border-[#3b82f6] pb-1" : "text-slate-400 hover:text-slate-200 transition-colors"}>
                Approved
              </button>
            </nav>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden lg:flex items-center gap-2 px-4 py-2 bg-[#191f31] rounded-full border border-white/8">
              <Search size={14} className="text-[#3b82f6]" />
              <input className="bg-transparent border-none focus:ring-0 text-sm w-44 placeholder:text-slate-500 outline-none" placeholder="Search campaign assets..." type="text" />
            </div>
            <button className="text-slate-400 hover:text-[#3b82f6] transition-colors">
              <Bell size={18} />
            </button>
            <div className="h-6 w-px bg-white/10 mx-1" />
            {!IS_TENANT_MODE ? (
              <button onClick={handleRunAgent} disabled={agentRunning}
                className="px-5 py-2 bg-gradient-to-br from-[#3b82f6] to-blue-500 text-slate-950 rounded-full font-['Space_Grotesk'] font-semibold text-sm hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-60 flex items-center gap-1.5">
                {agentRunning ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
                {agentRunning ? "Running…" : "Run Agent"}
              </button>
            ) : (
              <Link to="/signal-inbox"
                className="px-5 py-2 bg-gradient-to-br from-[#3b82f6] to-blue-500 text-slate-950 rounded-full font-['Space_Grotesk'] font-semibold text-sm hover:scale-[1.02] active:scale-95 transition-all flex items-center gap-1.5">
                <TrendingUp size={14} /> Upgrade
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* ── Left Sidebar ──────────────────────────────────────────────────────── */}
      <aside className="fixed left-0 top-0 h-full w-64 z-40 bg-[#151b2d] hidden lg:flex flex-col pt-24 pb-8 px-4">
        <div className="px-4 mb-8">
          <h2 className="text-white font-['Space_Grotesk'] font-medium text-lg tracking-tight">Queue Control</h2>
          <p className="text-slate-500 text-xs font-medium mt-0.5">Marketing Agent v2.5</p>
        </div>
        <nav className="flex-1 space-y-1">
          {[
            { id: "pending",   icon: Clock,       label: "Pending" },
            { id: "approved",  icon: CheckCircle, label: "Approved" },
            { id: "rejected",  icon: XCircle,     label: "Rejected" },
            { id: "analytics", icon: BarChart2,   label: "Analytics" },
            { id: "hot_leads", icon: Zap,         label: "Hot Leads", dot: hotLeads.length > 0 },
          ].map(({ id, icon: Icon, label, dot }) => (
            <button key={id} onClick={() => setView(id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                view === id
                  ? "bg-gradient-to-r from-[#3b82f6]/10 to-transparent text-[#3b82f6] border-r-2 border-[#3b82f6]"
                  : "text-slate-400 hover:text-slate-100 hover:bg-[#191f31]"
              }`}>
              <Icon size={16} /> {label}
              {dot && <span className="ml-auto w-2 h-2 rounded-full bg-red-500 shrink-0" />}
            </button>
          ))}
        </nav>
        <div className="mt-auto space-y-4 pt-4 border-t border-white/8">
          <button onClick={handleBulkApprove}
            className="w-full py-3 px-4 bg-gradient-to-br from-[#3b82f6] to-blue-500 text-slate-950 rounded-xl font-['Space_Grotesk'] font-bold text-sm shadow-[0_4px_20px_rgba(78,222,163,0.2)] hover:scale-[1.02] transition-all">
            Bulk Approve
          </button>
          <div className="space-y-1">
            <Link to="/" className="flex items-center gap-3 px-4 py-2 text-slate-400 hover:text-slate-100 text-sm rounded-lg transition-colors">
              <HelpCircle size={15} /> Help
            </Link>
            <Link to="/" className="flex items-center gap-3 px-4 py-2 text-slate-400 hover:text-slate-100 text-sm rounded-lg transition-colors">
              <LogOut size={15} /> Exit
            </Link>
          </div>
        </div>
      </aside>

      {/* ── Main Content ──────────────────────────────────────────────────────── */}
      <main className="lg:ml-64 pt-24 min-h-screen px-6 lg:px-8 pb-16">

        {/* Page Title */}
        <section className="flex flex-col md:flex-row justify-between items-start md:items-end mb-10 gap-6">
          <div>
            <span className="text-[#3b82f6] text-[10px] tracking-[0.2em] uppercase mb-2 block font-['Space_Grotesk']">
              {IS_TENANT_MODE ? "Signal Inbox" : "Sovereign Intelligence"}
            </span>
            <h1 className="font-['Space_Grotesk'] text-4xl lg:text-5xl font-light tracking-tight text-white">
              {IS_TENANT_MODE ? (
                <>Signal Inbox <span className="text-slate-500">/</span>{" "}
                <span className="text-[#3b82f6] font-medium">Dashboard</span></>
              ) : (
                <>Campaign Intelligence <span className="text-slate-500">/</span>{" "}
                <span className="text-[#3b82f6] font-medium">Command Center</span></>
              )}
            </h1>
          </div>
          <div className="flex gap-3">
            <button onClick={() => fetchDrafts(view)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 hover:border-blue-500/40 transition-all text-sm font-medium text-slate-300">
              <RefreshCw size={15} /> Refresh
            </button>
            {!IS_TENANT_MODE && (
              <button onClick={handleRunEmails} disabled={emailRunning}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#23293c] border border-blue-500/20 hover:border-blue-500/50 transition-all text-sm font-medium text-slate-200 disabled:opacity-60">
                {emailRunning ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} className="text-[#3b82f6]" />}
                Run Emails
              </button>
            )}
            {!IS_TENANT_MODE && (
              <button onClick={handleRunAgent} disabled={agentRunning}
                className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-gradient-to-br from-[#3b82f6] to-blue-500 text-slate-950 font-bold text-sm shadow-[0_8px_30px_rgba(78,222,163,0.15)] hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-60">
                {agentRunning ? <Loader2 size={15} className="animate-spin" /> : <Zap size={15} />}
                Run Agent
              </button>
            )}
            {IS_TENANT_MODE && (
              <Link to="/signal-inbox"
                className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-br from-[#3b82f6] to-blue-500 text-slate-950 font-bold text-sm hover:scale-[1.02] active:scale-95 transition-all">
                Upgrade Plan
              </Link>
            )}
          </div>
        </section>

        {/* ── Tenant Dashboard Summary (Signal Inbox subscribers only) ── */}
        {IS_TENANT_MODE && dashMetrics && (
          <section className="mb-8 space-y-4">
            {/* Weekly metrics */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: "Leads Found This Week", value: dashMetrics.this_week?.leads_found || 0, icon: "🔍", color: "text-blue-400" },
                { label: "High-Signal Leads", value: dashMetrics.this_week?.high_signal || 0, icon: "🔥", color: "text-orange-400" },
                { label: "Drafts Approved", value: dashMetrics.this_week?.approved || 0, icon: "✅", color: "text-green-400" },
                { label: "Posted to Reddit", value: dashMetrics.this_week?.posted || 0, icon: "🚀", color: "text-purple-400" },
              ].map(({ label, value, icon, color }) => (
                <div key={label} className="bg-slate-900/70 border border-white/8 rounded-2xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg">{icon}</span>
                    <span className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">{label}</span>
                  </div>
                  <p className={`text-3xl font-bold ${color}`}>{value}</p>
                </div>
              ))}
            </div>

            {/* All-time performance + trending */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* All-time stats */}
              <div className="bg-slate-900/70 border border-white/8 rounded-2xl p-5">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-3">All-Time Performance</p>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: "Total Drafts", value: dashMetrics.all_time?.total_drafts || 0 },
                    { label: "Total Posted", value: dashMetrics.all_time?.total_posted || 0 },
                    { label: "Avg Pain Score", value: dashMetrics.all_time?.avg_pain_score || 0 },
                  ].map(({ label, value }) => (
                    <div key={label} className="text-center">
                      <p className="text-xl font-bold text-white">{value}</p>
                      <p className="text-[10px] text-slate-500">{label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Trending pain points */}
              <div className="bg-slate-900/70 border border-white/8 rounded-2xl p-5">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-3">Trending in Your Industries</p>
                {dashMetrics.trending_pain_points?.length > 0 ? (
                  <div className="space-y-2">
                    {dashMetrics.trending_pain_points.slice(0, 4).map((t, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <span className="text-sm text-slate-300 capitalize">{t.vertical}</span>
                        <span className="text-xs text-blue-400 font-semibold">{t.post_count} posts this week</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 italic">Collecting data — trends will appear after the first full week</p>
                )}
              </div>
            </div>

            {/* Recent activity feed */}
            {dashMetrics.recent_activity?.length > 0 && (
              <div className="bg-slate-900/70 border border-white/8 rounded-2xl p-5">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-3">Recent Activity</p>
                <div className="space-y-2">
                  {dashMetrics.recent_activity.slice(0, 5).map((a) => (
                    <div key={a.id} className="flex items-center gap-3 text-sm">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        a.status === 'fired' ? 'bg-green-500' :
                        a.status === 'approved' ? 'bg-blue-500' :
                        a.status === 'pending' ? 'bg-amber-500' : 'bg-slate-600'
                      }`} />
                      <span className="text-slate-400 capitalize text-xs w-16 flex-shrink-0">{a.vertical}</span>
                      <span className="text-slate-300 truncate flex-1">{a.content_preview}</span>
                      {a.pain_score && <span className="text-[10px] text-blue-400 flex-shrink-0">score: {a.pain_score}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* Analytics Strip — 6 cards (UPGRADE 3) */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-5 mb-10">
          {statsCards.map(({ label, value, badge, bar }) => (
            <div key={label} className="rounded-2xl border border-white/8 bg-[rgba(25,31,49,0.7)] backdrop-blur-xl p-6 relative overflow-hidden group">
              <div className="absolute -right-4 -top-4 w-24 h-24 bg-[#3b82f6]/5 rounded-full blur-3xl group-hover:bg-[#3b82f6]/10 transition-all" />
              <p className="text-slate-500 text-[10px] font-['Space_Grotesk'] tracking-widest uppercase mb-4">{label}</p>
              <div className="flex items-end justify-between">
                <span className="font-['Space_Grotesk'] text-3xl font-medium text-white">{value}</span>
                {bar ? (
                  <div className="h-1.5 w-16 bg-white/8 rounded-full overflow-hidden">
                    <div className="h-full w-[65%] bg-gradient-to-r from-[#3b82f6] to-blue-500 rounded-full" />
                  </div>
                ) : (
                  <span className="text-[#3b82f6] text-xs font-bold bg-[#3b82f6]/10 px-2.5 py-1 rounded-full">{badge}</span>
                )}
              </div>
            </div>
          ))}
        </section>

        {/* Vertical Spike Alert — only renders when a spike is detected */}
        {topSpike && (
          <div className="mb-6 flex items-center gap-3 px-6 py-3 rounded-2xl bg-amber-500/10 border border-amber-500/25 text-amber-300 text-sm font-medium">
            <span>📈</span>
            <span>
              VERTICAL SPIKE: <strong>{topSpike.vertical}</strong> activity up{" "}
              <strong>{topSpike.spike_pct}%</strong> this week — agent prioritizing this vertical
            </span>
          </div>
        )}

        {/* Usage / Trial Banner — only renders when subscription data exists */}
        {usageData && (() => {
          const { tier, drafts_used_this_month: used, drafts_limit: limit,
                  days_remaining_in_trial: daysLeft, upgrade_url: upgradeUrl } = usageData;
          const isTrial   = tier === "trial";
          const pct       = limit > 0 ? used / limit : 0;
          const showTrial = isTrial && daysLeft != null && daysLeft <= 7;
          const showLimit = !showTrial && limit > 0 && pct >= 0.8;

          if (!showTrial && !showLimit) return null;

          return showTrial ? (
            <div className="mb-6 flex items-center gap-3 px-6 py-3 rounded-2xl bg-amber-500/10 border border-amber-500/25 text-amber-300 text-xs font-medium">
              <span>⚡</span>
              <span>
                {daysLeft === 0 ? "Last day of your free trial" : `${daysLeft} day${daysLeft === 1 ? "" : "s"} left in your free trial`}
                {" — "}{used}/{limit} drafts used.
              </span>
              <a
                href={upgradeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-auto shrink-0 px-3 py-1 rounded-full bg-amber-400/20 hover:bg-amber-400/30 text-amber-200 transition-colors whitespace-nowrap"
              >
                Upgrade to Outreach →
              </a>
            </div>
          ) : (
            <div className="mb-6 flex items-center gap-3 px-6 py-3 rounded-2xl bg-red-500/10 border border-red-500/25 text-red-300 text-xs font-medium">
              <span>🔴</span>
              <span>{used}/{limit} drafts used this month.</span>
              <a
                href={upgradeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-auto shrink-0 px-3 py-1 rounded-full bg-red-400/20 hover:bg-red-400/30 text-red-200 transition-colors whitespace-nowrap"
              >
                Upgrade for unlimited →
              </a>
            </div>
          );
        })()}

        {/* Weekly Review */}
        {perfData?.show_weekly_review && (
          <WeeklyReviewPanel perfData={perfData} onApproveTweaks={handleApproveTweaks} />
        )}

        {/* 2-col layout: Intake Terminal + Content Queue */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 items-start">

          {/* ── Intake Terminal (admin only) ──────────────────────────── */}
          {IS_TENANT_MODE ? (
            <section className="xl:col-span-4 rounded-2xl border border-white/10 bg-[rgba(25,31,49,0.7)] backdrop-blur-xl overflow-hidden flex flex-col p-6">
              <h3 className="font-['Space_Grotesk'] text-xl flex items-center gap-2 text-white mb-4">
                <Activity size={18} className="text-[#3b82f6]" /> Your Signal Inbox
              </h3>
              {usageData && (
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between text-slate-400">
                    <span>Plan</span>
                    <span className="text-white font-medium capitalize">{usageData.tier}</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>Drafts Used</span>
                    <span className="text-white font-medium">
                      {usageData.drafts_used_this_month}/{usageData.drafts_limit === -1 ? "Unlimited" : usageData.drafts_limit}
                    </span>
                  </div>
                  {usageData.days_remaining_in_trial != null && (
                    <div className="flex justify-between text-slate-400">
                      <span>Trial Days Left</span>
                      <span className="text-amber-400 font-medium">{usageData.days_remaining_in_trial}</span>
                    </div>
                  )}
                </div>
              )}
              <Link to="/signal-inbox"
                className="mt-6 w-full py-3 bg-gradient-to-br from-[#3b82f6] to-blue-500 text-slate-950 rounded-full font-['Space_Grotesk'] font-bold text-sm text-center hover:scale-[1.01] transition-all block">
                Manage Subscription
              </Link>
            </section>
          ) : (
          <section className="xl:col-span-4 rounded-2xl border border-white/10 bg-[rgba(25,31,49,0.7)] backdrop-blur-xl overflow-hidden flex flex-col">
            <div className="p-6 border-b border-white/8 bg-[#151b2d]/50">
              <h3 className="font-['Space_Grotesk'] text-xl flex items-center gap-2 text-white">
                <Terminal size={18} className="text-[#3b82f6]" /> Intake Terminal
              </h3>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <label className="text-[10px] font-['Space_Grotesk'] text-slate-500 mb-2 block uppercase tracking-wider">Target Channel</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { val: "reddit_reply",  label: "Reddit",   icon: MessageSquare },
                    { val: "linkedin_post", label: "LinkedIn (Soon)", icon: Briefcase, disabled: true },
                    { val: "email",         label: "Email",    icon: Mail },
                    { val: "blog",          label: "Blog",     icon: Globe },
                  ].map(({ val, label, icon: Icon, disabled }) => (
                    <button key={val} onClick={() => !disabled && setManualChannel(val)}
                      disabled={disabled}
                      className={`flex items-center justify-center gap-2 py-2 px-3 rounded-xl text-sm font-medium transition-all ${
                        disabled
                          ? "bg-[#191f31] border border-white/5 text-slate-600 cursor-not-allowed opacity-50"
                          : manualChannel === val
                          ? "bg-[#191f31] border border-[#3b82f6]/40 text-[#3b82f6]"
                          : "bg-[#191f31] border border-white/8 text-slate-400 hover:border-white/20 hover:text-slate-200"
                      }`}>
                      <Icon size={13} /> {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[10px] font-['Space_Grotesk'] text-slate-500 mb-2 block uppercase tracking-wider">Vertical</label>
                <select value={manualVertical} onChange={(e) => setManualVertical(e.target.value)}
                  className="w-full bg-slate-950/50 border border-white/15 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500/50">
                  {["general","restaurant","dental","real_estate","legal","ecommerce","hvac","accounting"].map(v => (
                    <option key={v} value={v} style={{ background: "#0f172a" }}>{v.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-['Space_Grotesk'] text-slate-500 mb-2 block uppercase tracking-wider">Contextual Input</label>
                <textarea
                  className="w-full bg-slate-950/50 border-b border-white/15 focus:border-blue-500/50 transition-all focus:ring-0 outline-none text-sm placeholder:text-slate-600 p-4 rounded-xl text-white resize-none"
                  placeholder="Paste source material, brief, or competitor URLs…"
                  rows={6}
                  value={manualCtx}
                  onChange={(e) => setManualCtx(e.target.value)}
                />
              </div>
              <button onClick={handleManualGenerate} disabled={generating || !manualCtx.trim()}
                className="w-full py-4 bg-gradient-to-br from-[#3b82f6] to-blue-500 text-slate-950 rounded-full font-['Space_Grotesk'] font-bold text-sm shadow-[0_10px_40px_rgba(78,222,163,0.1)] hover:scale-[1.01] transition-all disabled:opacity-40 flex items-center justify-center gap-2">
                {generating ? <><Loader2 size={14} className="animate-spin" /> Generating…</> : <><Zap size={14} /> Generate Intelligence Draft</>}
              </button>
            </div>
          </section>
          )}

          {/* ── Content Queue ─────────────────────────────────────────────── */}
          <section className={IS_TENANT_MODE ? "xl:col-span-8 space-y-6" : "xl:col-span-8 space-y-6"}>
            {/* Status Tabs */}
            <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-none">
              {TABS.map(({ id, label, dot }) => (
                <button key={id} onClick={() => setView(id)}
                  className={`relative px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                    view === id
                      ? "bg-[#3b82f6]/10 text-[#3b82f6] border border-[#3b82f6]/25 font-bold"
                      : "text-slate-500 hover:text-slate-200 transition-all"
                  }`}>
                  {label}
                  {dot && <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500" />}
                </button>
              ))}
            </div>

            {/* Draft Feed */}
            {loading ? (
              <div className="flex items-center justify-center h-48 text-slate-600">
                <Loader2 size={20} className="animate-spin mr-2 text-[#3b82f6]" /> Loading…
              </div>
            ) : view === "analytics" ? (
              <AnalyticsView perfData={perfData} convData={convData} onRunAgent={handleRunAgent} onSetVertical={setManualVertical} />
            ) : view === "hot_leads" ? (
              hotLeads.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 text-slate-600 border border-white/8 rounded-2xl bg-[rgba(25,31,49,0.7)] backdrop-blur-xl">
                  <Zap size={24} className="mb-2" />
                  <p className="text-sm">No hot leads yet</p>
                  <p className="text-xs text-slate-700 mt-1">Run the agent to scan for positive replies</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {hotLeads.map(lead => (
                    <HotLeadCard
                      key={lead._id}
                      lead={lead}
                      onDraftDm={handleDraftDm}
                      onDismiss={handleDismissLead}
                    />
                  ))}
                </div>
              )
            ) : sortedDrafts.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-slate-600 border border-white/8 rounded-2xl bg-[rgba(25,31,49,0.7)] backdrop-blur-xl">
                <AlertCircle size={24} className="mb-2" />
                <p className="text-sm">No {view} drafts</p>
                {view === "pending" && (
                  <button onClick={handleRunAgent} className="mt-3 text-xs text-[#3b82f6] hover:underline">
                    Run agent to generate new drafts →
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-5">
                {sortedDrafts.map((draft) => (
                  <DraftCard
                    key={draft._id}
                    draft={draft}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    onEdit={handleEdit}
                    onRegenerate={handleRegenerate}
                    onMarkOutcome={handleMarkOutcome}
                    onRetry={view === "fire_failed" ? handleRetryFire : undefined}
                    isApproved={view === "approved" || view === "fired"}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
